import * as React from 'react';

import { Slot } from '@radix-ui/react-slot';
import {
  IMAGE_ASPECT_CLASSES,
  IMAGE_FRAME_CLASSES,
  IMAGE_INSET_PADDING_CLASSES,
  IMAGE_OBJECT_FIT_CLASSES,
  IMAGE_OVERLAY_POSITION_CLASSES,
  imageRadiusClass,
  imageVariants,
  type ImageAspectToken,
  type ImageFrameMode,
  type ImageInsetPaddingToken,
  type ImageOverlayPosition,
  type ImageRoundedSides,
  type ImageVariantProps,
} from './image.variants';
import { getActivatableProps } from '@/hen-ui/lib/activatable';
import { cn, composeRefs, warnOnce } from '@/hen-ui/utils';

export interface ImageProps
  extends React.ImgHTMLAttributes<HTMLImageElement>,
    ImageVariantProps {
  /** When true, merges the image into its child (Radix Slot). */
  asChild?: boolean;
  /** Which sides the rounding applies to (the whole edge, not one corner). Default all four. */
  roundedSides?: ImageRoundedSides;
  /**
   * Media-frame padding. `bleed` fills the box edge-to-edge; `inset` uses
   * `insetPadding` inside the frame. Not an overlay.
   */
  frame?: ImageFrameMode;
  /**
   * Space around the photo when `frame` is `inset`. Ignored on bleed.
   * Default `sm` matches the original inset size.
   */
  insetPadding?: ImageInsetPaddingToken;
  /**
   * Public aspect ratio for a stable box. `auto` keeps intrinsic sizing.
   * Use this (and parent width) for frames that reflow. `width`/`height`
   * become a CSS size on the frame only; unframed photos keep HTML
   * attributes so existing pages do not change size.
   */
  aspect?: ImageAspectToken;
  /** Draws a token-backed outline around the photo. Off by default. */
  border?: boolean;
  /**
   * Content pinned over a corner of the photo, such as a save button. On an
   * unframed photo, `className`, `style`, `id`, `width` and `height` size the
   * box that holds the photo and its corner content, as on a framed photo.
   */
  overlay?: React.ReactNode;
  /** Corner the overlay sits in. Default `top-right`. */
  overlayPosition?: ImageOverlayPosition;
  /**
   * Editor canvas only. Goes on the outermost element (the photo, or the
   * frame or box around it), while `ref` stays on the photo.
   */
  editorRootRef?: React.Ref<HTMLElement>;
}

/**
 * Focus for a bound photo: a 2px focus-colour edge with a white 2px ring just
 * inside it, both following the corner radius. Outlines and borders are the
 * only edges that paint over an `<img>` (an inset shadow sits under the
 * photo), so a bare photo uses a border plus an inset outline.
 */
const BOUND_IMAGE_CLASS_NAME =
  'cursor-pointer focus-visible:border-[2px] focus-visible:border-solid focus-visible:border-focus focus-visible:outline focus-visible:outline-2 focus-visible:outline-white focus-visible:-outline-offset-4';

/**
 * Holds the focus border's space at rest so focusing a bare photo does not
 * resize it and move the content around it. A photo with `border` keeps its
 * own 1px border at rest instead.
 */
const BOUND_IMAGE_RESERVED_BORDER_CLASS_NAME =
  'border-[2px] border-solid border-transparent';

/**
 * The same look on a frame, which carries the semantics when present: the
 * frame's outline is the focus-colour edge and the photo's outline is the
 * white ring, so nothing changes size on focus.
 */
const BOUND_IMAGE_FRAME_CLASS_NAME =
  'cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-focus focus-visible:-outline-offset-2 [&>img]:focus-visible:outline [&>img]:focus-visible:outline-2 [&>img]:focus-visible:outline-white [&>img]:focus-visible:-outline-offset-4';

function toCssLength(value: string | number | undefined): string | undefined {
  if (value == null || value === '') return undefined;
  if (typeof value === 'number') return `${value}px`;
  const trimmed = value.trim();
  if (trimmed === '') return undefined;
  return /^-?\d+(\.\d+)?$/.test(trimmed) ? `${trimmed}px` : trimmed;
}

function hasUtility(className: string | undefined, utility: string): boolean {
  if (!className) return false;
  return className.split(/\s+/).includes(utility);
}

/** React 18 drops camelCase `fetchPriority` on host nodes; HTML uses `fetchpriority`. */
function imgFetchPriorityAttr(
  fetchPriority: React.ImgHTMLAttributes<HTMLImageElement>['fetchPriority'],
):
  | {
      fetchpriority: NonNullable<
        React.ImgHTMLAttributes<HTMLImageElement>['fetchPriority']
      >;
    }
  | undefined {
  if (fetchPriority == null) return undefined;
  return { fetchpriority: fetchPriority };
}

/**
 * Image component following HEN Design System standards.
 *
 * A semantic img element with HEN-specific variants and design tokens.
 * For fixed logo dimensions and card chrome, use the LogoBox composite.
 *
 * @example
 * // Rounded image
 * <Image
 *   rounded="xl"
 *   src="/images/banner.jpg"
 *   alt="Banner image"
 * />
 *
 * @example
 * // Framed thumbnail with top-only radius and a stable aspect box
 * <Image
 *   rounded="md"
 *   roundedSides="top"
 *   frame="bleed"
 *   aspect="4/3"
 *   src="/images/thumb.jpg"
 *   alt="Thumbnail"
 * />
 *
 * @example
 * // With a click action the image becomes a keyboard-operable button named
 * // by its alt text. A framed image puts the button on the frame.
 * <Image src="/images/photo.jpg" alt="Sample photo" onClick={openDetails} />
 */
export const Image = React.forwardRef<HTMLImageElement, ImageProps>(
  (
    {
      className,
      rounded,
      objectFit,
      asChild,
      height,
      width,
      style,
      roundedSides = 'all',
      frame = 'bleed',
      insetPadding = 'sm',
      aspect = 'auto',
      border = false,
      id,
      fetchPriority,
      overlay,
      overlayPosition = 'top-right',
      editorRootRef,
      onClick,
      onKeyDown,
      onKeyUp,
      ...props
    },
    ref,
  ) => {
    const needsFrame = aspect !== 'auto' || frame === 'inset';
    const hasOverlay = overlay != null && overlay !== false;
    const boxedOverlay = hasOverlay && !needsFrame;
    const photoIsRoot = !needsFrame && !boxedOverlay;
    const rootRef = React.useMemo(
      () =>
        editorRootRef ? composeRefs<HTMLElement>(editorRootRef) : undefined,
      [editorRootRef],
    );
    const photoRef = React.useMemo(
      () =>
        photoIsRoot && editorRootRef
          ? composeRefs<HTMLElement>(ref, editorRootRef)
          : ref,
      [photoIsRoot, ref, editorRootRef],
    );
    const isBound = !asChild && onClick != null;
    if (
      process.env.NODE_ENV !== 'production' &&
      isBound &&
      !props.alt?.trim()
    ) {
      warnOnce(
        'Image/click-action-without-alt',
        '[hen-ui/Image] An Image with a click action has no `alt` text, so its button has no accessible name. Set `alt`.',
      );
    }
    const eventProps = {
      onClick,
      ...(isBound
        ? getActivatableProps({ onClick, onKeyDown, onKeyUp })
        : { onKeyDown, onKeyUp }),
    };
    /**
     * The frame is the button only when it holds nothing else interactive.
     * Corner content can be a button, so with it the photo is the button
     * instead and the corner content stays a sibling with its own clicks.
     */
    const frameIsTrigger = isBound && needsFrame && !hasOverlay;
    const photoIsTrigger = isBound && !frameIsTrigger;
    /**
     * Literal 1px rather than `border-1`: that utility resolves to
     * `var(--border-1, 1)`, and the unitless fallback is invalid for
     * border-width unless the host app defines the token.
     */
    const borderClass = border
      ? 'border-[1px] border-solid border-neutral-100'
      : undefined;
    const useCvaRadius = roundedSides === 'all' && !needsFrame;
    const radiusClass = imageRadiusClass(rounded, roundedSides);
    /**
     * A bordered bleed frame already clips the photo, and its inner curve is
     * tighter than the frame's by the border width. Rounding the photo too
     * leaves a sliver of frame showing at each corner.
     */
    const frameClipsPhoto = needsFrame && frame === 'bleed' && border;
    const skipCssWidth = hasUtility(className, 'w-full');
    const skipCssHeight = hasUtility(className, 'h-full');
    const cssWidth = skipCssWidth ? undefined : toCssLength(width);
    const cssHeight = skipCssHeight ? undefined : toCssLength(height);
    const fillFrame =
      needsFrame && (aspect !== 'auto' || Boolean(cssWidth || cssHeight));
    const dimensionStyle: React.CSSProperties = {
      ...(cssWidth ? { width: cssWidth } : {}),
      ...(cssHeight ? { height: cssHeight } : {}),
    };

    const imgClassName = cn(
      useCvaRadius
        ? imageVariants({ objectFit, rounded })
        : [
            'transition-opacity duration-200',
            IMAGE_OBJECT_FIT_CLASSES[objectFit ?? 'cover'],
          ],
      !useCvaRadius && !frameClipsPhoto && radiusClass,
      fillFrame && 'h-full w-full max-h-full max-w-full min-h-0 min-w-0',
      photoIsRoot && !width && 'w-full',
      !needsFrame && borderClass,
      photoIsTrigger &&
        !(border && !needsFrame) &&
        BOUND_IMAGE_RESERVED_BORDER_CLASS_NAME,
      photoIsTrigger && BOUND_IMAGE_CLASS_NAME,
      photoIsRoot && className,
      boxedOverlay && 'block w-full h-full',
    );

    const Comp = asChild ? Slot : 'img';
    const imageEl = (
      <Comp
        {...props}
        {...(frameIsTrigger ? {} : eventProps)}
        {...imgFetchPriorityAttr(fetchPriority)}
        id={photoIsRoot ? id : undefined}
        className={imgClassName}
        height={needsFrame ? undefined : height}
        width={needsFrame ? undefined : width}
        style={photoIsRoot ? style : undefined}
        ref={photoRef}
      />
    );

    // Layer is pointer-events-none so the photo stays non-interactive; only the
    // child cluster re-enables events, so just the chip is clickable.
    const overlayEl = hasOverlay ? (
      <div
        data-slot='image-overlay'
        className={cn(
          'pointer-events-none absolute z-10',
          IMAGE_OVERLAY_POSITION_CLASSES[overlayPosition],
        )}
      >
        <div className='pointer-events-auto'>{overlay}</div>
      </div>
    ) : null;

    if (!needsFrame) {
      if (!overlayEl) return imageEl;
      return (
        <div
          ref={rootRef}
          id={id}
          className={cn('relative w-full', className)}
          style={{ ...dimensionStyle, ...style }}
        >
          {imageEl}
          {overlayEl}
        </div>
      );
    }

    const frameClassName = cn(
      'grid',
      IMAGE_FRAME_CLASSES[frame],
      frame === 'inset' && IMAGE_INSET_PADDING_CLASSES[insetPadding],
      IMAGE_ASPECT_CLASSES[aspect],
      radiusClass,
      fillFrame && !cssWidth && !width && 'w-full',
      !fillFrame && 'w-fit',
      cssWidth && 'shrink-0 max-w-full',
      overlayEl && 'relative',
      borderClass,
      frameIsTrigger && BOUND_IMAGE_FRAME_CLASS_NAME,
      className,
    );

    return (
      <div
        ref={rootRef}
        id={id}
        className={frameClassName}
        style={{ ...dimensionStyle, ...style }}
        {...(frameIsTrigger ? eventProps : {})}
      >
        {imageEl}
        {overlayEl}
      </div>
    );
  },
);

Image.displayName = 'Image';
